export const URL_GENERAL_DASHBOARD_PAGE = "/";

/**
 * Retired. `/login` now server-redirects to {@link URL_AUTH_LOGIN}; the stock Shesha login form is
 * gone. The path is kept alive only because `withAuth` defaults its unauthorised redirect to
 * `/login`, so gated routes under `src/app/(main)/` still aim here. Prefer URL_AUTH_LOGIN in new code.
 *
 * @deprecated Use {@link URL_AUTH_LOGIN}.
 */
export const URL_LOGIN_PAGE = "/login";

/**
 * The stock Shesha forgot-password form, rendered as a dynamic form under `/no-auth`. The public
 * portal no longer links here — it has its own {@link URL_AUTH_FORGOT_PASSWORD} screen in the member
 * design language. Kept because the admin portal still points at the same form.
 *
 * @deprecated In the public portal, use {@link URL_AUTH_FORGOT_PASSWORD}.
 */
export const URL_FORGOT_PASSWORD = "/no-auth/shesha/forgot-password?mode=edit";

/* ------------------------------------------------------------------------------------------------
 * Authentication.
 *
 * Every screen reachable WITHOUT a session lives under `/auth/*`, and every screen under `/member/*`
 * requires one. That split is the whole redirect rule: an anonymous visitor to any `/member/*` route
 * is sent to {@link URL_AUTH_LOGIN}, and `/auth/*` is the only place they are left alone.
 *
 * The paths carry NO route-group segment: Next.js route groups are invisible in the URL, so
 * `src/app/(auth)/auth/login/page.tsx` serves `/auth/login`. The `(auth)` group exists only to keep
 * these screens outside the `withAuth`-wrapped shells — never reference a group name in a URL.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Prefix of the whole unauthenticated area, for `startsWith` checks that must cover login, register,
 * verify and forgot-password alike. Checking against {@link URL_AUTH_LOGIN} instead would treat the
 * register form as a protected page — see `MemberSessionWatcher`.
 */
export const URL_AUTH_PREFIX = "/auth";

/** Sign-in card. Also the unauthorised-redirect target for every gated member screen. */
export const URL_AUTH_LOGIN = "/auth/login";
export const URL_AUTH_REGISTER = "/auth/register";
export const URL_AUTH_VERIFY = "/auth/verify";
export const URL_AUTH_FORGOT_PASSWORD = "/auth/forgot-password";

/**
 * Where both one-time-pin email links land — registration confirmation and password reset alike.
 *
 * NOT under `/auth`, and that is not an oversight: this is the url the live email templates already
 * contain, so the branded screen took the path over instead of the templates being reissued. See
 * `screens/member/auth/EmailLinkScreen`. Nothing in the portal should LINK here — a member only ever
 * arrives from their inbox — so this constant exists for tests and for `startsWith` checks.
 */
export const URL_EMAIL_LINK_VERIFICATION = "/no-auth/shesha/email-link-verification";

/* ------------------------------------------------------------------------------------------------
 * Member portal. Everything below requires a session.
 * ---------------------------------------------------------------------------------------------- */

export const URL_MEMBER_DASHBOARD = "/member/dashboard";
export const URL_MEMBER_EVENTS = "/member/events";
export const URL_MEMBER_BENEFITS = "/member/benefits";
export const URL_MEMBER_PROFILE = "/member/profile";
export const URL_MEMBER_PAYMENT = "/member/payment";

/** Invoice / receipt for one membership payment. */
export const urlMemberInvoice = (invoiceId: string): string => `/member/invoice/${invoiceId}`;

/** Prefix of the invoice route, for `startsWith` checks that must not know the id. */
export const URL_MEMBER_INVOICE_PREFIX = "/member/invoice";

/**
 * Routes that are a step in a flow rather than a section of the portal. `MemberChrome` hides the
 * pill-nav row on these so a member mid-payment is not invited to wander off (spec §4.0).
 *
 * `URL_AUTH_PREFIX` covers login, register, verify and forgot-password in one entry — none of them
 * is a section, and all four are reachable without a session.
 */
export const MEMBER_FLOW_ROUTES: readonly string[] = [
    URL_AUTH_PREFIX,
    URL_MEMBER_PAYMENT,
    URL_MEMBER_INVOICE_PREFIX,
];
